import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CAMPAIGN_ITEM_SELECTION_MODES, type CampaignCreateInput } from './schemas';

import { useClients } from '@/clients/queries';
import { FormField } from '@/components/form-field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWarehouses } from '@/warehouses/queries';

export interface CampaignFormFieldsProps {
  mode: 'create' | 'edit';
}

export const CampaignFormFields = ({ mode }: CampaignFormFieldsProps) => {
  const { control } = useFormContext<CampaignCreateInput>();
  const clientId = useWatch<CampaignCreateInput>({ control, name: 'clientId' });
  const { data: clients } = useClients();
  const { data: warehouses } = useWarehouses(
    typeof clientId === 'string' && clientId ? { clientId } : {},
  );

  return (
    <>
      {mode === 'create' ? (
        <>
          <div className="space-y-2">
            <Label>Клиент</Label>
            <Controller
              control={control}
              name="clientId"
              render={({ field, fieldState }) => (
                <>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите клиента…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(clients ?? []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldState.error ? (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  ) : null}
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>Склад</Label>
            <Controller
              control={control}
              name="warehouseId"
              render={({ field, fieldState }) => (
                <>
                  <Select value={field.value} onValueChange={field.onChange} disabled={!clientId}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={clientId ? 'Выберите склад…' : 'Сначала выберите клиента'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(warehouses ?? []).map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldState.error ? (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  ) : null}
                </>
              )}
            />
          </div>
        </>
      ) : null}

      <FormField<CampaignCreateInput> name="name" label="Название" placeholder="Summer items Q2" />

      <div className="space-y-2">
        <Label>Сообщение</Label>
        <Controller
          control={control}
          name="messageText"
          render={({ field, fieldState }) => (
            <>
              <textarea
                value={field.value}
                onChange={field.onChange}
                rows={4}
                maxLength={500}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Текст, который уйдёт в trade-offer сообщение…"
              />
              {fieldState.error ? (
                <p className="text-xs text-destructive">{fieldState.error.message}</p>
              ) : null}
            </>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label>Режим выбора предметов</Label>
        <Controller
          control={control}
          name="itemSelectionMode"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_ITEM_SELECTION_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
    </>
  );
};
